/**
 * E45 — string catalogue, German-first. Pure data (NO react import): the API
 * test suite imports this file directly to run the per-locale claim-lock scan
 * over every string in both locales.
 *
 * Rules (Team S, non-negotiable):
 *  - DE is the default locale; EN is the secondary.
 *  - Keys must exist in BOTH locales (guard-tested: identical key sets).
 *  - CLAIM SURFACES (`claims.*`): EN is pinned verbatim to the E36-approved
 *    constants; DE carries the German wording ROMINA APPROVED 2026-07-29
 *    (PROPOSED-DE-CLAIM-TRANSLATIONS.md, approved revision — incl. the
 *    "Zugangsbeschränktes" amendment). Both locales guard-tested verbatim.
 *  - German PV terminology follows the generated documents (Flächen, Belegung,
 *    Ertrag, Wirtschaftlichkeit, Netzanmeldung, Marktstammdatenregister …).
 */

export const CLAIM_SURFACE_KEYS = ["claims.positioning", "claims.pilotScope"] as const;

const de = {
  /* ---- claim surfaces (E36) — GERMAN WORDING APPROVED BY ROMINA 2026-07-29 ----
   * Pinned VERBATIM per PROPOSED-DE-CLAIM-TRANSLATIONS.md (approved revision).
   * Amendment applied: "Zugangsbeschränktes" (access-restricted by explicit grant);
   * the superseded draft wording is banned from code — guard-tested in i18n-governance.test.ts.
   * No paraphrasing, no reflow. */
  "claims.positioning":
    "Professionelle PV-Planung, Simulation, Wirtschaftlichkeitsrechnung und Dokumentation in einem Arbeitsablauf.",
  "claims.pilotScope":
    "Zugangsbeschränktes Design-Partner-Pilotprogramm. Die Ergebnisse sind Planungs- und Dokumentationsunterstützung. Die abschließende technische, rechtliche, netzseitige und kaufmännische Prüfung verbleibt beim verantwortlichen Fachbetrieb bzw. Unternehmen.",

  /* ---- app frame ---- */
  "app.nav.projects": "Projekte",
  "app.nav.signIn": "Anmeldung",
  "app.signOut": "Abmelden",
  "app.back": "Zurück",

  /* ---- auth ---- */
  "auth.signIn": "Anmelden",
  "auth.createAccount": "Firmenkonto anlegen",
  "auth.resetPassword": "Passwort zurücksetzen",
  "auth.setNewPassword": "Neues Passwort festlegen",
  "auth.yourName": "Ihr Name",
  "auth.companyName": "Firmenname",
  "auth.email": "E-Mail",
  "auth.password": "Passwort",
  "auth.newPassword": "Neues Passwort",
  "auth.minChars": "(mind. 10 Zeichen)",
  "auth.signInAction": "Anmelden",
  "auth.createAction": "Konto anlegen",
  "auth.requestLink": "Link anfordern",
  "auth.savePassword": "Passwort speichern",
  "auth.noAccount": "Noch kein Konto?",
  "auth.registerCompany": "Firma registrieren",
  "auth.forgotPassword": "Passwort vergessen?",
  "auth.alreadyRegistered": "Bereits registriert?",
  "auth.emailVerified": "E-Mail-Adresse bestätigt — Sie können sich jetzt anmelden.",
  /* E50 — MUST equal the API's pinned TOO_MANY_ATTEMPTS_MESSAGE verbatim (cross-pinned) */
  "auth.tooManyAttempts": "Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment und versuchen Sie es dann erneut.",
  /* E49 — invite signup (front door for the closed pilot) */
  "auth.inviteWelcome": "Ihre Einladung ist gültig — legen Sie Ihr Firmenkonto an.",
  "auth.inviteEmailLocked": "Diese Einladung ist an die angezeigte E-Mail-Adresse gebunden.",
  "auth.inviteUsed": "Diese Einladung wurde bereits verwendet. Wenn Sie bereits ein Konto angelegt haben, melden Sie sich an — andernfalls fordern Sie bitte eine neue Einladung an.",
  "auth.inviteExpired": "Diese Einladung ist abgelaufen. Bitte fordern Sie eine neue Einladung an.",
  "auth.inviteUnknown": "Dieser Einladungslink ist ungültig. Bitte prüfen Sie den Link aus Ihrer E-Mail oder fordern Sie eine neue Einladung an.",
  "auth.passwordChanged": "Passwort geändert — bitte melden Sie sich mit dem neuen Passwort an.",
  "auth.noOrganization": "Diesem Konto ist keine Organisation zugeordnet.",

  /* ---- dashboard shell ---- */
  "dash.tab.projects": "Projekte",
  "dash.tab.projects.sub": "Alle PV-Projekte Ihrer Organisation",
  "dash.tab.pipeline": "Pipeline",
  "dash.tab.pipeline.sub": "Leads & Projekte nach Phase — Ihr Vertriebsboard",
  "dash.tab.widget": "Lead-Widget",
  "dash.tab.widget.sub": "White-Label-Rechner für Ihre Website",
  "dash.tab.prices": "Preisbuch",
  "dash.tab.prices.sub": "Ihre Einkaufspreise, Lohnsätze & Marge",
  "dash.tab.integrations": "Integrationen",
  "dash.tab.integrations.sub": "Datenquellen, Anbieter & Verbindungen",
  "dash.myProjects": "Meine Projekte",
  "dash.kpi.projects": "Projekte",
  "dash.kpi.customers": "Kunden",
  "dash.kpi.openLeads": "Offene Leads",
  "dash.kpi.accepted": "Beauftragt / in Betrieb",
  "dash.addProject": "Projekt anlegen",
  "dash.search": "🔍 Suchen…",
  "dash.col.name": "Name",
  "dash.col.customer": "Kunde",
  "dash.col.type": "Typ",
  "dash.col.status": "Status",
  "dash.col.updated": "Geändert",
  "dash.empty": "☰ Noch keine Projekte — klicken Sie auf „Projekt anlegen“.",
  "dash.salesPipeline": "Vertriebspipeline",
  "dash.pipelineHint": "Leads Phase für Phase weiterschieben; gewonnene Leads werden zu Projekten.",
  "dash.widgetLead": "🧲 Widget",

  /* ---- lead drawer ---- */
  "lead.title": "Lead",
  "lead.noEmail": "keine E-Mail",
  "lead.noPhone": "kein Telefon",
  "lead.widgetLead": "🧲 Widget-Lead",
  "lead.widgetEstimate": "Widget-Schätzung:",
  "lead.savings": "Ersparnis",
  "lead.payback": "Amortisation",
  "lead.perYear": "€/Jahr",
  "lead.years": "J.",
  "lead.roof": "Dach",
  "lead.wantsBattery": "Speicher gewünscht",
  "lead.followUps": "Wiedervorlagen",
  "lead.noneYet": "Noch keine.",
  "lead.overdue": "⚠ überfällig",
  "lead.due": "Fällig",
  "lead.kind": "Art",
  "lead.kind.call": "Anruf",
  "lead.kind.email": "E-Mail",
  "lead.kind.visit": "Vor-Ort-Termin",
  "lead.kind.other": "Sonstiges",
  "lead.note": "Notiz",
  "lead.notePlaceholder": "Was ist zu tun…",
  "lead.addFollowUp": "＋ Wiedervorlage anlegen",
  "lead.createProject": "⚡ Projekt aus diesem Lead erstellen",
  "lead.projectPrefix": "PV",

  /* ---- price book ---- */
  "prices.title": "Preisbuch",
  "prices.hint":
    "Überschreiben Sie beliebige Preise: Ihre Komponenten-EK/VK, Lohn- und Montagesätze, Marge. Leeres Feld = Plattform-Standard (grau angezeigt). Änderungen gelten für jede neue Simulation, Kalkulation und jedes neue Angebot — bereits unterschriebene Angebote bleiben unberührt.",
  "prices.ratesTitle": "Sätze & Marge",
  "prices.componentsTitle": "Komponentenpreise (Ihr EK/VK statt Listenpreis)",
  "prices.col.component": "Komponente",
  "prices.col.list": "Liste €",
  "prices.col.yours": "Ihr Preis €",
  "prices.saved": "✓ Gespeichert — jede neue Simulation und jedes neue Angebot verwendet jetzt Ihre Preise.",
  "prices.save": "Speichern",
  "prices.changes": "Änderung(en)",
  "prices.rate.labor.residential": "Montagelohn — Wohngebäude",
  "prices.rate.labor.commercial": "Montagelohn — Gewerbe",
  "prices.rate.electrical.residential": "Elektro-BOS — Wohngebäude",
  "prices.rate.electrical.commercial": "Elektro-BOS — Gewerbe",
  "prices.rate.mounting.pitched_roof": "Unterkonstruktion — Schrägdach",
  "prices.rate.mounting.flat_roof": "Unterkonstruktion — Flachdach (Ballast)",
  "prices.rate.mounting.ground": "Unterkonstruktion — Freifläche",
  "prices.rate.laborPerModule": "Lohn je Modul",
  "prices.rate.scaffolding": "Gerüst (Schrägdach, pauschal)",
  "prices.rate.batteryInstall": "Speicherinstallation (pauschal)",
  "prices.rate.planning.residential": "Planung & Dokumentation — Wohngebäude",
  "prices.rate.margin.pct": "Unternehmensmarge",
  "prices.rate.regional.factor": "Regionaler Kostenfaktor",
  "prices.unit.perKwp": "€/kWp",
  "prices.unit.perModule": "€/Modul",
  "prices.unit.eur": "€",
  "prices.unit.pct": "%",
  "prices.unit.factor": "×",

  /* ---- widget tab ---- */
  "widget.title": "Lead-Erfassungs-Widget",
  "widget.hint":
    "Fügen Sie dieses Snippet in Ihre Website ein. Besucher erhalten eine Sofort-Einschätzung; die Anfrage landet als Lead in Ihrer Pipeline — mit Dachfläche, Verbrauch und der angezeigten Schätzung.",
  "widget.embedTitle": "Ihr Einbettungscode",
  "widget.copied": "✓ Kopiert",
  "widget.copy": "⧉ Snippet kopieren",
  "widget.keyLabel": "Widget-Schlüssel:",
  "widget.loading": "wird geladen…",
  "widget.keyHint": "— öffentlich unbedenklich: er kann ausschließlich Leads in Ihrer Organisation anlegen (ratenbegrenzt, mit Spam-Schutz).",
  "widget.demoHint1": "Demoseite: öffnen Sie",
  "widget.demoHint2": "nach dem Widget-Build",
  "widget.demoHint3": "in",

  /* ---- new project modal ---- */
  "newproject.title": "Neues PV-Projekt",
  "newproject.name": "Projektname",
  "newproject.namePlaceholder": "z. B. EFH Schmidt",
  "newproject.customer": "Kunde",
  "newproject.newCustomer": "— neuer Kunde —",
  "newproject.newCustomerName": "Name des neuen Kunden",
  "newproject.newCustomerPlaceholder": "Nachname / Firma",
  "newproject.type": "Projekttyp",
  "newproject.alsoLead": "Zusätzlich einen Lead in der Vertriebspipeline anlegen",
  "newproject.cancel": "Abbrechen",
  "newproject.create": "Projekt anlegen →",
  "newproject.defaultName": "Neues Projekt",

  /* ---- project types ---- */
  "ptype.RESIDENTIAL_PITCHED": "Wohngebäude — Schrägdach",
  "ptype.RESIDENTIAL_FLAT": "Wohngebäude — Flachdach",
  "ptype.COMMERCIAL_FLAT": "Gewerbe — Flachdach",
  "ptype.COMMERCIAL_PITCHED": "Gewerbe — Schrägdach",
  "ptype.INDUSTRIAL": "Industriedach",
  "ptype.AGRICULTURAL": "Landwirtschaft",
  "ptype.CARPORT": "Carport",
  "ptype.PARKING_LOT": "Parkplatz",
  "ptype.GROUND_MOUNTED": "Freifläche",
  "ptype.UTILITY_SCALE": "Utility-Scale",
  "ptype.MIXED": "Gemischt",

  /* ---- project statuses ---- */
  "pstatus.FEASIBILITY": "Machbarkeit",
  "pstatus.DESIGN": "Planung",
  "pstatus.TECHNICAL_REVIEW": "Technische Prüfung",
  "pstatus.PROPOSAL_SENT": "Angebot versendet",
  "pstatus.ACCEPTED": "Beauftragt",
  "pstatus.PERMITTING": "Genehmigung",
  "pstatus.PROCUREMENT": "Beschaffung",
  "pstatus.INSTALLATION_PLANNED": "Montage geplant",
  "pstatus.INSTALLING": "In Montage",
  "pstatus.COMMISSIONING": "Inbetriebnahme",
  "pstatus.OPERATING": "In Betrieb",
  "pstatus.MAINTENANCE": "Wartung",
  "pstatus.COMPLETED": "Abgeschlossen",
  "pstatus.CANCELLED": "Storniert",

  /* ---- lead stages ---- */
  "lstage.NEW": "Neu",
  "lstage.CONTACTED": "Kontaktiert",
  "lstage.QUALIFIED": "Qualifiziert",
  "lstage.SITE_ASSESSMENT": "Vor-Ort-Prüfung",
  "lstage.PROPOSAL": "Angebot",
  "lstage.NEGOTIATION": "Verhandlung",
  "lstage.WON": "Gewonnen",
  "lstage.LOST": "Verloren",

  /* ---- integrations ---- */
  "integrations.title": "Datenquellen & Integrationen",
  "integrations.hint":
    "Die Plattform wählt automatisch die beste ausreichende Quelle — freie und offene Daten zuerst. Kostenpflichtige Anbieter werden nur nach ausdrücklicher Freigabe genutzt. Plan:",
  "integrations.col.provider": "Anbieter",
  "integrations.col.type": "Typ",
  "integrations.col.confidence": "Konfidenz",
  "integrations.col.cost": "Kosten",
  "integrations.col.status": "Status",
  "integrations.col.config": "Konfiguration",
  "integrations.active": "aktiv",
  "integrations.coming": "geplant",
  "integrations.locked": "gesperrt",
  "integrations.enabled": "aktiviert",
  "integrations.keySet": "Schlüssel hinterlegt ✓ (ersetzen…)",
  "integrations.keyPlaceholder": "API-Schlüssel…",
  "integrations.save": "Speichern",
  "integrations.lockedHint":
    "Gesperrte Anbieter sind durch Plan oder Anbieterverträge begrenzt — sie werden angezeigt, damit Sie wissen, was möglich ist, und laufen nie ohne Freigabe. Jede Anbieterauswahl und jede Freigabe kostenpflichtiger Daten wird in der Entscheidungshistorie des Projekts protokolliert.",

  /* ---- common ---- */
  "common.error": "Fehler",
  "common.save": "Speichern",
  "common.cancel": "Abbrechen",
  "common.close": "Schließen",
  "common.delete": "Löschen",
  "common.loading": "wird geladen…",

  /* ---- canvas (E42 on-map controls) ---- */
  "canvas.zoomIn": "Vergrößern",
  "canvas.zoomOut": "Verkleinern",
  "canvas.fit": "Auf Flächen einpassen",
  "canvas.undoPoint": "Letzten Punkt rückgängig (Strg/Cmd+Z)",
  "canvas.undoPointShort": "↶ Punkt",
  "canvas.mapControls": "Kartensteuerung",
  // E60 — Ansicht drehen (Kamera) ist NICHT die Modulraster-Ausrichtung:
  // beide Begriffe bleiben unterscheidbar, nie beide nur „Drehung".
  "canvas.rotateViewLeft": "Ansicht nach links drehen",
  "canvas.rotateViewRight": "Ansicht nach rechts drehen",
  "canvas.resetNorth": "Ansicht nach Norden ausrichten",
  // E62.1 — the imagery has a resolution ceiling; the planner must know which
  // limit they've hit ({res} = source's ground resolution in cm)
  "canvas.nativeResLimit": "Maximale Auflösung des Luftbilds erreicht — {res} cm/Pixel",
  "canvas.nativeResLimitGeneric": "Maximale Auflösung des Luftbilds erreicht",
  // E63.3 — an edited official outline is planner geometry; the label says so
  "site.plannerModified": "planerisch bearbeitet",

  /* ---- E47: export gating made visible BEFORE the click ---- */
  "gate.unverified": "Exporte sind noch gesperrt: Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse über den zugesandten Link.",
  "gate.unverifiedRemedy": "E-Mail-Adresse bestätigen",
  "gate.demo": "Exporte sind in der Demo-Auswertung gesperrt — Pilotzugang erforderlich.",
  "gate.demoRemedy": "Pilotzugang erforderlich",
  "gate.resend": "Bestätigungs-E-Mail erneut senden",
  "gate.resendSent": "Bestätigungs-E-Mail versendet — bitte Posteingang prüfen.",

  /* ---- project workspace: navigation + rollup ---- */
  "proj.sect.plan": "Planen",
  "proj.sect.configure": "Konfigurieren",
  "proj.sect.deliver": "Liefern",
  "proj.sect.execute": "Ausführen",
  "proj.step.overview": "Übersicht",
  "proj.step.site": "Standort & Dach",
  "proj.step.design": "Auslegung",
  "proj.step.package": "Zubehör",
  "proj.step.results": "Simulation",
  "proj.step.permits": "Regulierung (DE)",
  "proj.step.proposals": "Angebote",
  "proj.step.procurement": "Beschaffung",
  "proj.step.installation": "Montage",
  "proj.allProjects": "← Alle Projekte",
  "proj.rollup.modules": "Module",
  "proj.rollup.kwhYear": "kWh / Jahr",
  "proj.rollup.investment": "Investition",
  "proj.rollup.payback": "Amortisation",
  "proj.rollup.status": "STATUS",
  "proj.yearsShort": "J.",

  /* ---- overview ---- */
  "proj.ov.noSite": "Noch kein Standort erfasst",
  "proj.ov.systemSize": "Anlagengröße",
  "proj.ov.modulesSuffix": "Module",
  "proj.ov.simulateToCompute": "(Simulation ausführen)",
  "proj.ov.annualProduction": "Erwarteter Jahresertrag",
  "proj.ov.investmentNet": "Investition (netto)",
  "proj.ov.payback": "Amortisation",
  "proj.ov.progress": "Projektfortschritt",
  "proj.ov.siteDrawn": "Standort & Dachflächen erfasst",
  "proj.ov.designConfigured": "Auslegung konfiguriert",
  "proj.ov.simulationRun": "Simulation ausgeführt",
  "proj.ov.regChecklist": "Regulierungs-Checkliste",
  "proj.ov.proposalCreated": "Angebot erstellt",
  "proj.ov.done": "Erledigt",
  "proj.ov.open": "Offen — klicken zum Fortfahren",
  "proj.ov.continueSite": "Weiter: Standort & Dach →",

  /* ---- geo sources card ---- */
  "geo.title": "🛰️ Geometriequellen (Digitaler Zwilling)",
  "geo.hint": "Hybrid-Pipeline: beste verfügbare Daten je Projekt — eigene Uploads → offene Geodaten der Länder → Google Solar → OSM+KI → manuell. Kostenpflichtige Quellen werden mit Kosten angezeigt und nie automatisch bezogen.",
  "geo.evaluate": "Quellen bewerten",
  "geo.reconstruct": "▶ Rekonstruieren & beste Quelle anwenden",
  "geo.higherAccuracy": "Höhere Genauigkeit verfügbar:",
  "geo.estimatedCost": "Geschätzte Kosten:",
  "geo.expectedBenefit": "Erwarteter Nutzen: Konfidenz",
  "geo.optional": "Optional — für die finale Auslegung nutzen.",
  "geo.recommended": "Für genehmigungsreife Planung empfohlen.",
  "geo.approvePurchase": "Kauf freigeben (Admin)",
  "geo.approvalNote": "Erfordert ausdrückliche Freigabe — nie automatischer Bezug.",
  "geo.col.source": "Quelle",
  "geo.col.status": "Status",
  "geo.col.cost": "Kosten",
  "geo.available": "verfügbar",
  "geo.needsContract": "Vertrag erforderlich",
  "geo.coming": "geplant",
  "geo.noData": "keine Daten",
  "geo.openData": "Open Data · 0 €",
  "geo.ownData": "eigene Daten · 0 €",
  "geo.freeQuota": "freies Kontingent",
  "geo.perReport": "€/Bericht",
  "geo.subscription": "Abonnement",
  "geo.quote": "auf Anfrage",

  /* ---- site panel ---- */
  "site.title": "Standort & Dachflächen",
  "site.hint": "Gebäude lokalisieren, dann jede nutzbare Dach-/Freifläche einzeichnen. Modulraster drehen, bis die Reihen am First des Fotos ausgerichtet sind. Ziehen = verschieben, Scrollen = Zoom (oder die Karten-Buttons), Eckpunkte lassen sich ziehen, ↶ / Strg+Z = letzten Punkt zurücknehmen, Rechtsklick entfernt Eckpunkte und Hindernisse.",
  "site.address": "Adresse",
  "site.addressPlaceholder": "Straße Hausnummer, Ort",
  "site.find": "📍 Suchen",
  "site.state": "Bundesland",
  "site.consumption": "Verbrauch (kWh/Jahr)",
  "site.elPrice": "Strompreis (ct/kWh)",
  "site.loadProfile": "Lastprofil",
  "site.profile.H0": "H0 — Haushalt",
  "site.profile.G0": "G0 — Gewerbe (allgemein)",
  "site.profile.G1": "G1 — Gewerbe werktags Mo–Fr",
  "site.profile.L0": "L0 — Landwirtschaft",
  "site.deleteSurface": "Fläche löschen",
  "site.addSurface": "＋ Fläche hinzufügen",
  "site.detectBuilding": "🏠 Gebäude erkennen",
  "site.detectTitle": "Realen Gebäudeumriss aus OpenStreetMap importieren (ohne Schlüssel)",
  "site.aiSegments": "✨ KI-Dachsegmente",
  "site.aiTitle": "KI-Dachsegmente mit Neigung & Ausrichtung (Google Solar API, Schlüssel erforderlich)",
  "site.surfaceName": "Flächenname",
  "site.kind.pitched_roof": "Schrägdach",
  "site.kind.flat_roof": "Flachdach",
  "site.kind.ground": "Freifläche",
  "site.kind.carport": "Carport",
  "site.kind.facade": "Fassade",
  "site.roofN": "Dach",
  "site.drawBoundary": "✏️ Umriss zeichnen",
  "site.obstacle": "⛔ Hindernis",
  "site.azimuth": "Azimut",
  "site.tilt": "Neigung",
  "site.grid": "↻ Modulraster", // E60: explicit — never confusable with „Ansicht drehen" (Kamera)
  "site.setback": "Randabstand",
  "site.clearSurface": "Fläche leeren",
  "site.legendSurfaces": "Fläche(n) · aktiv:",
  "site.legendPts": "Punkte",
  "site.legendObstacles": "Hindernisse",
  "site.imagery": "Luftbild: Esri World Imagery (live)",
  "site.save": "Standort & Flächen speichern",
  "site.continueDesign": "Weiter: Auslegung →",
  "site.saved": "✓ Standort & Flächen gespeichert.",
  "site.addressNotFound": "Adresse nicht gefunden — Schreibweise prüfen oder Koordinaten manuell setzen.",
  "site.detected": "✓ {n} Fläche(n) erkannt via {src} — Ecken am Luftbild anpassen, dann speichern.",
  "site.detectedOsm": "OpenStreetMap-Umriss",
  "site.detectedGoogle": "Google Solar",
  // E66: the banner must name the TRUE source — official results were
  // captioned as OSM because only two providers were known here
  "site.detectedOfficial": "amtliche Geometrie (Landesvermessung)",
  // E67 — the chain says why better sources didn't answer
  "site.detectLoading": "Amtliche Geometrie wird geladen — der erste Abruf einer Kachel kann bis zu einer Minute dauern…",
  // E69 — the geocoder resolved a DIFFERENT postcode than typed (wrong Marktplatz)
  "site.plzMismatch": "Achtung: Die Adresse wurde mit PLZ {resolved} aufgelöst, eingegeben wurde {typed}. Bitte Kartenposition prüfen, bevor Sie fortfahren.",
  // E70 — Google liefert je Segment nur eine Bounding-Box, keinen Umriss
  "site.googleApproxExtent": "ungefähre Ausdehnung — Umriss vor der Belegung anpassen",
  "site.chainOfficialNoTile": "amtliche Geometrie: keine Kachel für diesen Standort",
  "site.chainOfficialNotReady": "amtliche Geometrie: für dieses Bundesland noch nicht freigeschaltet",
  "site.chainGoogleNotConfigured": "Google Solar: nicht konfiguriert",
  "site.chainGoogleNoCoverage": "Google Solar: keine Abdeckung an diesem Standort",
  /* E54 — honest ambiguity: ask instead of guessing */
  "site.pickBuilding": "Mehrere Gebäude kommen für diese Adresse infrage — die Umrisse sind auf dem Luftbild eingezeichnet. Bitte wählen Sie das richtige Gebäude.",
  "site.candidate": "Kandidat",
  "site.pickThis": "Dieses Gebäude übernehmen",
  "site.candidateMeta": "{area} m² Dachfläche · {planes} Dachfläche(n) · {dist} m vom Adresspunkt",
  /* E41 — MUST equal the API's pinned DETECT_FALLBACK_MESSAGE verbatim (cross-pinned in geodetect.test.ts) */
  "site.detectUnavailable":
    "Automatische Gebäudeerkennung ist derzeit nicht verfügbar — bitte zeichnen Sie den Umriss manuell. Alle Planungsfunktionen stehen unverändert zur Verfügung.",

  /* ---- design panel ---- */
  "design.title": "Auslegung — Komponenten & Belegung",
  "design.hint": "Alle Dachflächen speisen einen Wechselrichter. Klick auf ein Modul entfernt es; Modul ziehen verschiebt es (rot = unzulässige Position). Das Raster folgt der Drehung jeder Fläche aus dem Standort-Schritt.",
  "design.noSurfaces": "Noch keine Flächen gezeichnet — zuerst zurück zu Standort & Dach.",
  "design.edit": "BEARBEITEN",
  "design.portrait": "▯ Hochformat",
  "design.landscape": "▭ Querformat",
  "design.rowGap": "Reihenabstand",
  "design.reset": "↺ Zurücksetzen ({removed} entfernt, {moved} verschoben)",
  "design.modules": "Module",
  "design.legendClick": "Klick = entfernen/wiederherstellen · Ziehen = verschieben (1-cm-Raster) · rot = unzulässig",
  "design.pvModule": "PV-Modul",
  "design.powerClass": "Leistungsklasse",
  "design.inverter": "Wechselrichter",
  "design.integratedIn": "⚡ Integriert in",
  "design.integratedMeta": "kW AC Hybrid · {n} MPPT · im Speicherpreis enthalten",
  "design.hybrid": "Hybrid",
  "design.string": "String",
  "design.suggested": "Empfohlen: {n}× für {kwp} kWp gesamt (DC/AC {ratio})",
  "design.batteryOperation": "Speicher & Betrieb",
  "design.noBattery": "Kein Speicher",
  "design.noBatteryMeta": "Gesamten Überschuss einspeisen",
  "design.hybridIncl": "Hybrid inkl. {kw} kW Wechselrichter",
  "design.acCoupled": "AC-gekoppelt",
  "design.dispatch": "Betriebsstrategie Speicher",
  "design.dispatch.self": "Eigenverbrauch maximieren (fester Tarif)",
  "design.dispatch.price": "Preisoptimiert (dynamischer Tarif, z. B. Tibber/aWATTar)",
  "design.mlpeTitle": "🔬 Modulelektronik (MLPE)",
  "design.mlpeHint": "Mikrowechselrichter ersetzen den String-Wechselrichter vollständig (AC-Module, MPPT je Modul, keine Serienstrings — AC-gekoppelter Speicher erforderlich). Optimierer arbeiten mit einem String-Wechselrichter und holen Serien-Mismatch-Verluste auf verschatteten Dächern zurück. SMA ShadeFix und Fronius Dynamic Peak Manager sind Wechselrichter-Firmware — bei diesen Marken ohne Mehrkosten aktiv.",
  "design.micro": "⚡ Mikrowechselrichter (ersetzt String-WR)",
  "design.microNone": "— keiner (String-Wechselrichter) —",
  "design.perUnit": "€/Stück",
  "design.optimizer": "🔧 DC-Optimierer (je Modul, mit String-WR)",
  "design.optimizerNone": "— keiner —",
  "design.requires": "erfordert",
  "design.anyInverter": "beliebiger WR",
  "design.perModule": "€/Modul",
  "design.mlpeAsk": "🧠 Braucht diese Auslegung MLPE? (KI-Ingenieur)",
  "design.saveFirst": "zuerst eine Auslegung speichern",
  "design.mlpeSummary": "Verschattungsstreuung {shade} % → Serien-Mismatch {mismatch} % bei String-Architektur. Basis: {arch}, {kwh} kWh/a, Kapitalwert {npv}.",
  "design.col.option": "Option",
  "design.col.gain": "Mehrertrag",
  "design.col.addedCost": "Mehrkosten",
  "design.col.npv": "ΔKapitalwert",
  "design.col.payback": "ΔAmortisation",
  "design.col.verdict": "Bewertung",
  "design.requiredArch": "erforderlich (Architektur)",
  "design.applyRecommendation": "Empfehlung in Auslegung übernehmen",
  "design.ecoTitle": "🏠 Energie-Ökosystem",
  "design.ecoHint": "Wallbox, Wärmepumpe, Energiemanager und Zähler sind vollwertige Komponenten: sie verändern die stündliche Lastsimulation, Autarkie und Amortisation und fließen in Kalkulation, Stückliste und Bestellungen ein — genau wie Module, Wechselrichter und Speicher.",
  "design.evCharger": "🚗 Wallbox (Ladepunkt)",
  "design.none": "— keine —",
  "design.pvSurplus": "PV-Überschuss",
  "design.heatPump": "🔥 Wärmepumpe",
  "design.ems": "🧠 Energiemanager (EMS / Smart Home)",
  "design.allBrands": "alle Marken",
  "design.included": "enthalten",
  "design.meter": "⚡ Energiezähler",
  "design.meterGeneric": "— generisch (automatisch im Zubehör) —",
  "design.thermalKw": "kW th.",
  "design.adv.dcCoupled": "{model} ist DC-gekoppelt — Mikrowechselrichter-Anlagen benötigen AC-gekoppelte Speicher (z. B. sonnenBatterie).",
  "design.adv.optBrand": "{model} arbeitet nur mit {brand}-Wechselrichtern (Architekturkopplung).",
  "design.adv.solaredge": "Die SolarEdge-Architektur erfordert Optimierer an jedem Modul — S500B/S650B wählen.",
  "design.adv.emsBrand": "{model} steuert nur {brands}-Systeme — mit {brand} ein offenes EMS wählen (Home Assistant, OpenEMS, Loxone).",
  "design.adv.surplusCharging": "{model} unterstützt PV-Überschussladen — zum Aktivieren einen Energiemanager hinzufügen (verlagert ~50 % des Ladens in das Solarfenster).",
  "design.adv.sgReady": "{model} ist SG-Ready — ein Energiemanager ermöglicht PV-optimiertes Vorheizen.",
  "design.adv.surplusActive": "Überschusssteuerung aktiv: Wallbox-Laden und/oder Wärmepumpen-Vorheizen folgen in der Simulation der PV-Erzeugung.",
  "design.adv.notReleased": "{battery} ist für {inverter} nicht freigegeben. Kompatiblen Speicher oder ein AC-gekoppeltes System wählen (Tesla, sonnen).",
  "design.adv.containsOwn": "{model} enthält einen eigenen {kw}-kW-Hybridwechselrichter — kein separater PV-Wechselrichter nötig.",
  "design.adv.acWorksAny": "{model} ist AC-gekoppelt — arbeitet mit jedem Wechselrichter.",
  "design.adv.compatibleWith": "{model} ist kompatibel mit {inverter}.",
  "design.saveVariant": "Als neue Variante speichern",
  "design.saveDesign": "Auslegung speichern",
  "design.continueSim": "Weiter: Simulation →",
  "design.designSaved": "✓ Auslegung als neue Version gespeichert.",
  "design.versions": "Versionen:",
  "design.manual": "manuell",
  "design.variantName": "Variante",

  /* ---- AI auto-designer ---- */
  "ai.title": "✨ KI-Auto-Auslegung",
  "ai.hint": "Durchsucht den gesamten Katalog — Wechselrichter-Leistungsklassen (DC/AC-Ziel 1,2), Hybrid- und Speicherpaare passend zu Ihrem Verbrauch, integrierte Systeme wie Tesla Powerwall 3 und beide Betriebsstrategien. Jeder Kandidat durchläuft die volle 8.760-Stunden-Simulation und wird nach Kapitalwert gereiht. Übernehmen erzeugt eine neue Auslegungsvariante; Ihre manuelle Auslegung wird nie überschrieben.",
  "ai.objective": "Ziel",
  "ai.obj.npv": "Maximaler Lebenszeitgewinn (Kapitalwert)",
  "ai.obj.payback": "Schnellste Amortisation",
  "ai.obj.autarky": "Maximale Autarkie",
  "ai.obj.cost": "Geringste Investition",
  "ai.obj.production": "Maximaler Ertrag",
  "ai.budget": "Budgetgrenze € (optional)",
  "ai.budgetPlaceholder": "z. B. 20000",
  "ai.maxPayback": "Max. Amortisation Jahre (optional)",
  "ai.maxPaybackPlaceholder": "z. B. 6",
  "ai.run": "▶ KI-Ingenieur starten",
  "ai.rerun": "↻ KI-Ingenieur erneut starten",
  "ai.evaluated": "{n} Konfigurationen simuliert · Modul: {module} · Wetter: {weather}",
  "ai.synthetic": "synthetische Klimatologie",
  "ai.col.system": "System",
  "ai.col.autarky": "Autarkie",
  "ai.col.invest": "Investition",
  "ai.col.npv": "Kapitalwert",
  "ai.col.payback": "Amortisation",
  "ai.you": "Sie",
  "ai.yourBaseline": "Ihre manuelle Auslegung — Vergleichsbasis",
  "ai.best": "beste",
  "ai.stringsInvalid": "Strings unzulässig",
  "ai.outsideConstraints": "außerhalb Vorgaben",
  "ai.accept": "Als KI-Auslegung übernehmen",
  "ai.accepted": "Übernommen.",
  "ai.createdVariant": "„{name}“ als neue KI-Variante angelegt — Ihre manuelle Auslegung bleibt unberührt. Zum Prüfen Simulation oder Zubehör öffnen.",
  "ai.alternatives": "Empfohlene Alternativen & Abwägungen",
  "ai.sensitivity": "Sensitivität: Strompreis (Gewinner, vollständige Neusimulation)",
  "ai.col.scenario": "Preisszenario",
  "ai.col.benefit1": "Nutzen Jahr 1",
  "ai.today": "heute",

  /* ---- package panel ---- */
  "pkg.title": "Zubehör & Installationspaket",
  "pkg.hint": "Mengen werden aus Ihrer tatsächlichen Belegung abgeleitet ({info}) — Schienen aus Reihenlängen, Klemmen aus Segmenten, Stecker aus Strings, Schutztechnik aus dem Wechselrichter. Jede Position zeigt ihre Regel und bleibt editierbar.",
  "pkg.noDesign": "Zuerst eine Auslegung anlegen — das Paket wird aus Belegung und Stringplan abgeleitet.",
  "pkg.mainHardware": "Hauptkomponenten",
  "pkg.hardwareTotal": "Summe Hauptkomponenten",
  "pkg.cat.mounting": "Unterkonstruktion",
  "pkg.cat.dc": "DC-Elektrik",
  "pkg.cat.ac": "AC-Elektrik & Schutz",
  "pkg.cat.monitoring": "Monitoring & Speicher",
  "pkg.col.item": "Position",
  "pkg.col.qty": "Menge",
  "pkg.col.unit": "Einzel €",
  "pkg.col.total": "Summe €",
  "pkg.optional": "optional",
  "pkg.tileHardware": "Hauptkomponenten",
  "pkg.tileAccessories": "Zubehör & Kleinteile",
  "pkg.tileTotal": "Komplettes Installationspaket",
  "pkg.saveChanges": "{n} Änderung(en) speichern",
  "pkg.saved": "Gespeichert",
  "pkg.autoQty": "↺ Automatische Mengen",
  "pkg.continueSim": "Weiter: Simulation →",
  "pkg.priceHint": "Zubehörpreise sind Großhandels-Standardwerte — überschreiben Sie sie im Preisbuch (Schlüssel „accessory:<sku>“). Das vollständige Paket fließt in die Stücklisten-CSV und Ihre Bestellungen.",

  /* ---- results panel ---- */
  "sim.title": "Energie- & Wirtschaftlichkeitssimulation",
  "sim.hint": "Stündliche Simulation: PVGIS-Satelliteneinstrahlung → PV-Modell → Lastprofil → Speicherbetrieb → EEG-2026-Wirtschaftlichkeit (inkl. Solarspitzengesetz).",
  "sim.weather.pvgis": "PVGIS SARAH3 (Satellit, empfohlen)",
  "sim.weather.synthetic": "Synthetische Klimatologie (offline/Demo)",
  "sim.tariffTitle": "Anbieter dynamischer Tarif",
  "sim.tariff.generic": "Dynamischer Tarif: generisch",
  "sim.run": "▶ Simulation starten",
  "sim.running": "Simuliert…",
  "sim.createDesignFirst": "Zuerst eine Auslegung anlegen.",
  "sim.annualProduction": "Jahresertrag",
  "sim.performanceRatio": "Performance Ratio",
  "sim.selfSufficiency": "Autarkiegrad",
  "sim.selfConsumed": "Eigenverbrauch",
  "sim.gridFeedIn": "Netzeinspeisung",
  "sim.monthlyProduction": "Monatlicher Ertrag",
  "sim.payback": "Amortisation",
  "sim.investment": "Investition",
  "sim.vat0": ", 0 % USt.",
  "sim.benefitYear1": "Nutzen Jahr 1",
  "sim.npv25": "Kapitalwert (25 J.)",
  "sim.irr": "Interner Zinsfuß",
  "sim.lcoe": "Stromgestehung / kWh",
  "sim.cashflow": "Kumulierter Zahlungsstrom — 25 Jahre",
  "sim.dynTariff": "Analyse dynamischer Tarif — {provider} (EPEX Day-Ahead)",
  "sim.avgSpot": "Ø Spotpreis / kWh",
  "sim.negHours": "Stunden mit Negativpreis",
  "sim.s51Share": "Einspeisung unter § 51 (exakt)",
  "sim.importDynamic": "Bezugskosten dynamisch /Jahr (fest: {flat})",
  "sim.arbitrage": "Speicher-Arbitrage /Jahr",
  "sim.extraBenefit": "Mehrnutzen /Jahr ggü. festem Tarif",
  "sim.dynNote": "Die Einspeisevergütung schließt Viertelstunden mit negativen Preisen gemäß Solarspitzengesetz bereits aus — exakt aus der stündlichen Preisreihe berechnet, kein pauschaler Abschlag. Die Planungsreihe ist EPEX-kalibriert; im Betrieb werden aWATTar/ENTSO-E-Daten live angebunden.",
  "sim.inverterBanks": "Wechselrichter-Gruppen",
  "sim.col.bank": "Gruppe",
  "sim.col.inverter": "Wechselrichter",
  "sim.col.strings": "Strings",
  "sim.ok": "ok",
  "sim.check": "prüfen",
  "sim.electricalDesign": "Elektrische Auslegung",
  "sim.stringsLine": "{n} String(s) × {m} Module · Voc(kalt) {voc} V · DC/AC {ratio}",
  "sim.valid": "zulässig",
  "sim.invalid": "unzulässig",
  "sim.costBreakdown": "Kostenaufstellung",
  "sim.totalNet": "Summe (netto{vat})",
  "sim.vatCite": ", 0 % USt. § 12 UStG",
  "sim.bomCsv": "⬇ Stückliste CSV",
  "sim.cashflowCsv": "⬇ Zahlungsstrom CSV",
  "sim.continueReg": "Weiter: Regulierung →",

  /* ---- permits panel ---- */
  "perm.title": "Regulierungs- & Prozessassistent (DE)",
  "perm.hint": "Checkliste aus simulierter Anlagengröße, Projekttyp und Bundesland. Vorbereitungsunterstützung — die finale Freigabe liegt bei Netzbetreiber, Behörde und Elektrofachbetrieb.",
  "perm.generate": "⚙ Checkliste aus Simulation erzeugen",
  "perm.kind.DSO_GRID_APPLICATION": "Netzanmeldung (Netzbetreiber)",
  "perm.kind.MASTR_REGISTRATION": "Marktstammdatenregister",
  "perm.kind.SMART_METER_ORDER": "Intelligentes Messsystem (iMSys) + Steuerbox",
  "perm.kind.STRUCTURAL_CHECK": "Statik- / Dachlastprüfung",
  "perm.kind.BUILDING_PERMIT": "Baugenehmigung (B-Plan)",
  "perm.kind.DIRECT_MARKETING_CONTRACT": "Direktvermarktungsvertrag",
  "perm.kind.EEG_TENDER": "BNetzA-Ausschreibung (≥ 1 MWp)",
  "perm.kind.COMPLETION_NOTICE": "Fertigmeldung",
  "perm.kind.METER_CHANGE": "Zählerwechsel",
  "perm.pkgTitle": "📑 Netzanmeldungspaket",
  "perm.pkgHint": "3-seitiges PDF aus Ihrer aktuellen Auslegung: Netzbetreiber-Datenblatt (VDE-AR-N 4105) mit MaStR-Checkliste, Übersichtsschaltplan mit dimensionierten Leitungen und Schutzorganen (LS/SPD/gPV) sowie Stringplan + Leitungsliste mit Spannungsfallberechnung.",
  "perm.download": "⬇ Netzanmeldungspaket herunterladen (PDF)",
  "perm.continueProposals": "Weiter: Angebote →",

  /* ---- proposals panel ---- */
  "prop.title": "Angebote",
  "prop.hint": "PDF im Firmendesign mit Diagrammen und Unterschriftsblock; Versenden erzeugt einen öffentlichen E-Signatur-Link — die Annahme durch den Kunden setzt das Projekt automatisch auf BEAUFTRAGT.",
  "prop.create": "＋ Angebot aus letzter Simulation erstellen",
  "prop.simFirst": "Zuerst eine Simulation ausführen.",
  "prop.signingLink": "Signaturlink (an Ihren Kunden senden):",
  "prop.copy": "⧉ Kopieren",
  "prop.col.number": "Nummer",
  "prop.col.status": "Status",
  "prop.col.net": "Netto",
  "prop.col.gross": "Brutto",
  "prop.col.validUntil": "Gültig bis",
  "prop.pdf": "⬇ PDF",
  "prop.send": "✉ Zur Signatur senden",

  /* ---- procurement panel (EXECUTE — module deferred, strings translated) ---- */
  "proc.title": "Beschaffung",
  "proc.hint": "Ein Klick macht aus dem Installationspaket der Auslegung eine Bestellung für Ihren Großhändler — jedes Modul, jeder Wechselrichter, Speicher, jede Schiene, Klemme und jeder Stecker mit Nettopreisen aus Ihrem Preisbuch.",
  "proc.newOrder": "Neue Bestellung",
  "proc.supplierOptional": "— Lieferant (optional) —",
  "proc.addSupplierPlaceholder": "Lieferant hinzufügen…",
  "proc.add": "+ Hinzufügen",
  "proc.createOrder": "Bestellung aus Paket erstellen",
  "proc.created": "Bestellung aus dem vollständigen Installationspaket erstellt (Hauptkomponenten + gesamtes Zubehör).",
  "proc.order": "Bestellung",
  "proc.positions": "Positionen",
  "proc.net": "netto",
  "proc.noSupplier": "kein Lieferant",
  "proc.ordered": "bestellt",
  "proc.delivered": "geliefert",
  "proc.hide": "Ausblenden",
  "proc.poCsv": "⬇ Bestellung CSV",
  "proc.markOrdered": "Als bestellt markieren",
  "proc.markDelivered": "Als geliefert markieren",
  "proc.partialDelivery": "Teillieferung",
  "proc.cancel": "Stornieren",
  "proc.noOrders": "Noch keine Bestellungen.",
  "proc.continueInstall": "Weiter: Montage →",

  /* ---- installation panel (EXECUTE — module deferred, strings translated) ---- */
  "inst.title": "Montage",
  "inst.hint": "Team einplanen, Aufgabenliste bis zur Inbetriebnahme abarbeiten und die Daten des E.8-Protokolls erfassen.",
  "inst.schedule": "Terminplanung",
  "inst.crew": "Team",
  "inst.start": "Beginn",
  "inst.end": "Ende",
  "inst.updatePlan": "Plan aktualisieren",
  "inst.createPlan": "Plan + Aufgabenliste erstellen",
  "inst.planSaved": "Montageplan gespeichert — Aufgabenliste aus der Auslegung erzeugt (speicherbewusst).",
  "inst.taskBoard": "Aufgabenliste",
  "inst.done": "erledigt",
  "inst.status.OPEN": "Offen",
  "inst.status.IN_PROGRESS": "In Arbeit",
  "inst.status.DONE": "Erledigt",
  "inst.status.BLOCKED": "Blockiert",
  "inst.commissioning": "Inbetriebnahme (E.8)",
  "inst.commissioned": "in Betrieb genommen",
  "inst.electrician": "Elektrofachbetrieb",
  "inst.registrationNo": "Eintragungsnummer",
  "inst.meterNumber": "Zählernummer",
  "inst.mastrNumber": "MaStR-Nummer",
  "inst.save": "Speichern",
  "inst.markCommissioned": "Jetzt als in Betrieb genommen markieren",
  "inst.commissioningSaved": "Inbetriebnahmedaten gespeichert.",
  "inst.commissioningDone": "Inbetriebnahme erfasst — Frist für die MaStR-Registrierung beachten (1 Monat).",
  /* E44: locale-pinned date display */
  "inst.dateFormat": "TT.MM.JJJJ",
  "inst.plannedFor": "geplant",
} as const;

type MessageKeyInternal = keyof typeof de;

const en: Record<MessageKeyInternal, string> = {
  /* E36-approved English wording, LITERAL (the former `de[...]` aliasing would have
   * silently flipped the EN locale to German the moment the DE strings landed —
   * de-aliased per Romina's wiring order, 2026-07-29). Pinned to the @pv/documents
   * constants by i18n-governance.test.ts. */
  "claims.positioning": "Professional PV planning, simulation, financial modeling, and documentation workflow.",
  "claims.pilotScope":
    "Controlled design-partner pilot. Outputs are planning and documentation support. Final technical, legal, grid, and commercial review remains with the responsible professional or company.",

  "app.nav.projects": "Projects",
  "app.nav.signIn": "Sign in",
  "app.signOut": "Sign out",
  "app.back": "Back",

  "auth.signIn": "Sign in",
  "auth.createAccount": "Create your company account",
  "auth.resetPassword": "Reset password",
  "auth.setNewPassword": "Set a new password",
  "auth.yourName": "Your name",
  "auth.companyName": "Company name",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.newPassword": "New password",
  "auth.minChars": "(min. 10 characters)",
  "auth.signInAction": "Sign in",
  "auth.createAction": "Create account",
  "auth.requestLink": "Request link",
  "auth.savePassword": "Save password",
  "auth.noAccount": "No account?",
  "auth.registerCompany": "Register your company",
  "auth.forgotPassword": "Forgot password?",
  "auth.alreadyRegistered": "Already registered?",
  "auth.emailVerified": "Email address confirmed — you can sign in now.",
  "auth.tooManyAttempts": "Too many requests in a short time. Please wait a moment and try again.",
  "auth.inviteWelcome": "Your invitation is valid — create your company account.",
  "auth.inviteEmailLocked": "This invitation is bound to the email address shown.",
  "auth.inviteUsed": "This invitation has already been used. If you already created an account, sign in — otherwise please request a new invitation.",
  "auth.inviteExpired": "This invitation has expired. Please request a new invitation.",
  "auth.inviteUnknown": "This invitation link is not valid. Please check the link from your email or request a new invitation.",
  "auth.passwordChanged": "Password changed — please sign in with your new password.",
  "auth.noOrganization": "No organization is linked to this account.",

  "dash.tab.projects": "Projects",
  "dash.tab.projects.sub": "All PV projects of your organization",
  "dash.tab.pipeline": "Pipeline",
  "dash.tab.pipeline.sub": "Leads & projects by stage — your sales board",
  "dash.tab.widget": "Lead widget",
  "dash.tab.widget.sub": "White-label calculator for your website",
  "dash.tab.prices": "Price book",
  "dash.tab.prices.sub": "Your purchase prices, labor rates & margin",
  "dash.tab.integrations": "Integrations",
  "dash.tab.integrations.sub": "Data sources, providers & connections",
  "dash.myProjects": "My projects",
  "dash.kpi.projects": "Projects",
  "dash.kpi.customers": "Customers",
  "dash.kpi.openLeads": "Open leads",
  "dash.kpi.accepted": "Accepted / operating",
  "dash.addProject": "Add project",
  "dash.search": "🔍 Search…",
  "dash.col.name": "Name",
  "dash.col.customer": "Customer",
  "dash.col.type": "Type",
  "dash.col.status": "Status",
  "dash.col.updated": "Updated",
  "dash.empty": "☰ No projects yet — click “Add project”.",
  "dash.salesPipeline": "Sales pipeline",
  "dash.pipelineHint": "Advance leads stage by stage; won leads become projects.",
  "dash.widgetLead": "🧲 widget",

  "lead.title": "Lead",
  "lead.noEmail": "no email",
  "lead.noPhone": "no phone",
  "lead.widgetLead": "🧲 widget lead",
  "lead.widgetEstimate": "Widget estimate:",
  "lead.savings": "savings",
  "lead.payback": "payback",
  "lead.perYear": "€/yr",
  "lead.years": "yr",
  "lead.roof": "roof",
  "lead.wantsBattery": "wants battery",
  "lead.followUps": "Follow-ups",
  "lead.noneYet": "None yet.",
  "lead.overdue": "⚠ overdue",
  "lead.due": "Due",
  "lead.kind": "Kind",
  "lead.kind.call": "Call",
  "lead.kind.email": "Email",
  "lead.kind.visit": "Site visit",
  "lead.kind.other": "Other",
  "lead.note": "Note",
  "lead.notePlaceholder": "What to do…",
  "lead.addFollowUp": "＋ Add follow-up",
  "lead.createProject": "⚡ Create project from this lead",
  "lead.projectPrefix": "PV",

  "prices.title": "Price book",
  "prices.hint":
    "Overwrite any price: your component purchase/sales prices, labor and mounting rates, margin. Empty field = platform default (shown greyed). Changes apply to every new simulation, cost estimate and proposal — existing signed proposals are untouched.",
  "prices.ratesTitle": "Rates & margin",
  "prices.componentsTitle": "Component prices (your purchase price instead of list price)",
  "prices.col.component": "Component",
  "prices.col.list": "List €",
  "prices.col.yours": "Your price €",
  "prices.saved": "✓ Saved — every new simulation and proposal now uses your prices.",
  "prices.save": "Save",
  "prices.changes": "change(s)",
  "prices.rate.labor.residential": "Installation labor — residential",
  "prices.rate.labor.commercial": "Installation labor — commercial",
  "prices.rate.electrical.residential": "Electrical BOS — residential",
  "prices.rate.electrical.commercial": "Electrical BOS — commercial",
  "prices.rate.mounting.pitched_roof": "Mounting — pitched roof",
  "prices.rate.mounting.flat_roof": "Mounting — flat roof (ballast)",
  "prices.rate.mounting.ground": "Mounting — ground frames",
  "prices.rate.laborPerModule": "Labor per module",
  "prices.rate.scaffolding": "Scaffolding (pitched roof, fixed)",
  "prices.rate.batteryInstall": "Battery installation (fixed)",
  "prices.rate.planning.residential": "Planning & documentation — residential",
  "prices.rate.margin.pct": "Company margin",
  "prices.rate.regional.factor": "Regional cost factor",
  "prices.unit.perKwp": "€/kWp",
  "prices.unit.perModule": "€/module",
  "prices.unit.eur": "€",
  "prices.unit.pct": "%",
  "prices.unit.factor": "×",

  "widget.title": "Lead-capture widget",
  "widget.hint":
    "Paste this snippet into your website. Visitors get an instant solar estimate; their request lands in your Pipeline as a lead — with roof size, consumption and the shown estimate attached.",
  "widget.embedTitle": "Your embed code",
  "widget.copied": "✓ Copied",
  "widget.copy": "⧉ Copy snippet",
  "widget.keyLabel": "Widget key:",
  "widget.loading": "loading…",
  "widget.keyHint": "— public-safe: it can only create leads in your organization (rate-limited, spam-trapped).",
  "widget.demoHint1": "Demo page: open",
  "widget.demoHint2": "after building the widget",
  "widget.demoHint3": "in",

  "newproject.title": "New PV project",
  "newproject.name": "Project name",
  "newproject.namePlaceholder": "e.g. EFH Schmidt",
  "newproject.customer": "Customer",
  "newproject.newCustomer": "— new customer —",
  "newproject.newCustomerName": "New customer name",
  "newproject.newCustomerPlaceholder": "Last name / company",
  "newproject.type": "Project type",
  "newproject.alsoLead": "Also create a lead in the sales pipeline",
  "newproject.cancel": "Cancel",
  "newproject.create": "Create project →",
  "newproject.defaultName": "New project",

  "ptype.RESIDENTIAL_PITCHED": "Residential — pitched roof",
  "ptype.RESIDENTIAL_FLAT": "Residential — flat roof",
  "ptype.COMMERCIAL_FLAT": "Commercial — flat roof",
  "ptype.COMMERCIAL_PITCHED": "Commercial — pitched roof",
  "ptype.INDUSTRIAL": "Industrial rooftop",
  "ptype.AGRICULTURAL": "Agricultural",
  "ptype.CARPORT": "Carport",
  "ptype.PARKING_LOT": "Parking lot",
  "ptype.GROUND_MOUNTED": "Ground-mounted",
  "ptype.UTILITY_SCALE": "Utility scale",
  "ptype.MIXED": "Mixed",

  "pstatus.FEASIBILITY": "Feasibility",
  "pstatus.DESIGN": "Design",
  "pstatus.TECHNICAL_REVIEW": "Technical review",
  "pstatus.PROPOSAL_SENT": "Proposal sent",
  "pstatus.ACCEPTED": "Accepted",
  "pstatus.PERMITTING": "Permitting",
  "pstatus.PROCUREMENT": "Procurement",
  "pstatus.INSTALLATION_PLANNED": "Installation planned",
  "pstatus.INSTALLING": "Installing",
  "pstatus.COMMISSIONING": "Commissioning",
  "pstatus.OPERATING": "Operating",
  "pstatus.MAINTENANCE": "Maintenance",
  "pstatus.COMPLETED": "Completed",
  "pstatus.CANCELLED": "Cancelled",

  "lstage.NEW": "New",
  "lstage.CONTACTED": "Contacted",
  "lstage.QUALIFIED": "Qualified",
  "lstage.SITE_ASSESSMENT": "Site assessment",
  "lstage.PROPOSAL": "Proposal",
  "lstage.NEGOTIATION": "Negotiation",
  "lstage.WON": "Won",
  "lstage.LOST": "Lost",

  "integrations.title": "Data sources & integrations",
  "integrations.hint":
    "The platform always picks the best sufficient source automatically — free and open data first. Paid providers are only ever used after explicit approval. Plan:",
  "integrations.col.provider": "Provider",
  "integrations.col.type": "Type",
  "integrations.col.confidence": "Confidence",
  "integrations.col.cost": "Cost",
  "integrations.col.status": "Status",
  "integrations.col.config": "Configuration",
  "integrations.active": "active",
  "integrations.coming": "coming",
  "integrations.locked": "locked",
  "integrations.enabled": "enabled",
  "integrations.keySet": "key set ✓ (replace…)",
  "integrations.keyPlaceholder": "API key…",
  "integrations.save": "Save",
  "integrations.lockedHint":
    "Locked providers are gated by plan or provider contracts — they are shown so you know what is possible, and never run without approval. Every provider selection and paid-data approval is recorded in the project's engineering decision history.",

  "common.error": "Error",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.loading": "loading…",

  "canvas.zoomIn": "Zoom in",
  "canvas.zoomOut": "Zoom out",
  "canvas.fit": "Fit to surfaces",
  "canvas.undoPoint": "Undo last point (Ctrl/Cmd+Z)",
  "canvas.undoPointShort": "↶ Point",
  "canvas.mapControls": "Map controls",
  // E60 — view rotation (camera) is NOT the module-grid rotation: both stay
  // distinguishable, never both just "rotation".
  "canvas.rotateViewLeft": "Rotate view left",
  "canvas.rotateViewRight": "Rotate view right",
  "canvas.resetNorth": "Reset view to north",
  // E62.1 — the imagery has a resolution ceiling; the planner must know which
  // limit they've hit ({res} = source's ground resolution in cm)
  "canvas.nativeResLimit": "Maximum aerial image resolution reached — {res} cm/pixel",
  "canvas.nativeResLimitGeneric": "Maximum aerial image resolution reached",
  // E63.3 — an edited official outline is planner geometry; the label says so
  "site.plannerModified": "planner-modified",

  "gate.unverified": "Exports are still locked: please confirm your email address via the link we sent you.",
  "gate.unverifiedRemedy": "Confirm email address",
  "gate.demo": "Exports are locked in demo evaluation — pilot access required.",
  "gate.demoRemedy": "Pilot access required",
  "gate.resend": "Resend verification email",
  "gate.resendSent": "Verification email sent — please check your inbox.",

  "proj.sect.plan": "Plan",
  "proj.sect.configure": "Configure",
  "proj.sect.deliver": "Deliver",
  "proj.sect.execute": "Execute",
  "proj.step.overview": "Overview",
  "proj.step.site": "Site & Roof",
  "proj.step.design": "Design",
  "proj.step.package": "Accessories",
  "proj.step.results": "Simulation",
  "proj.step.permits": "Regulation (DE)",
  "proj.step.proposals": "Proposals",
  "proj.step.procurement": "Procurement",
  "proj.step.installation": "Installation",
  "proj.allProjects": "← All projects",
  "proj.rollup.modules": "Modules",
  "proj.rollup.kwhYear": "kWh / year",
  "proj.rollup.investment": "Investment",
  "proj.rollup.payback": "Payback",
  "proj.rollup.status": "STATUS",
  "proj.yearsShort": "yr",

  "proj.ov.noSite": "No site defined yet",
  "proj.ov.systemSize": "System size",
  "proj.ov.modulesSuffix": "modules",
  "proj.ov.simulateToCompute": "(simulate to compute)",
  "proj.ov.annualProduction": "Est. annual production",
  "proj.ov.investmentNet": "Investment (net)",
  "proj.ov.payback": "Payback",
  "proj.ov.progress": "Project progress",
  "proj.ov.siteDrawn": "Site & roof drawn",
  "proj.ov.designConfigured": "Design configured",
  "proj.ov.simulationRun": "Simulation run",
  "proj.ov.regChecklist": "Regulation checklist",
  "proj.ov.proposalCreated": "Proposal created",
  "proj.ov.done": "Done",
  "proj.ov.open": "Open — click to continue",
  "proj.ov.continueSite": "Continue: Site & Roof →",

  "geo.title": "🛰️ Geometry sources (Digital Twin)",
  "geo.hint": "Hybrid pipeline: best available data per project — uploads → open government geodata → Google Solar → OSM+AI → manual. Paid sources are shown with cost, never purchased automatically.",
  "geo.evaluate": "Evaluate sources",
  "geo.reconstruct": "▶ Reconstruct & apply best source",
  "geo.higherAccuracy": "Higher accuracy available:",
  "geo.estimatedCost": "Estimated cost:",
  "geo.expectedBenefit": "Expected benefit: confidence",
  "geo.optional": "Optional — use for final engineering.",
  "geo.recommended": "Recommended for permit-grade design.",
  "geo.approvePurchase": "Approve purchase (admin)",
  "geo.approvalNote": "Requires explicit approval — never purchased automatically.",
  "geo.col.source": "Source",
  "geo.col.status": "Status",
  "geo.col.cost": "Cost",
  "geo.available": "available",
  "geo.needsContract": "needs contract",
  "geo.coming": "coming",
  "geo.noData": "no data",
  "geo.openData": "Open Data · 0 €",
  "geo.ownData": "own data · 0 €",
  "geo.freeQuota": "free quota",
  "geo.perReport": "€/report",
  "geo.subscription": "subscription",
  "geo.quote": "quote",

  "site.title": "Site & Roof surfaces",
  "site.hint": "Locate the building, then draw each usable roof/land surface. Rotate the module grid until rows align with the ridge on the photo. Drag = pan, scroll = zoom (or the on-map buttons), corner points are draggable, ↶ / Ctrl+Z = undo last point, right-click removes corner points and obstacles.",
  "site.address": "Address",
  "site.addressPlaceholder": "Street number, city",
  "site.find": "📍 Find",
  "site.state": "State (Bundesland)",
  "site.consumption": "Consumption (kWh/yr)",
  "site.elPrice": "El. price (ct/kWh)",
  "site.loadProfile": "Load profile",
  "site.profile.H0": "H0 — Household",
  "site.profile.G0": "G0 — Commerce (general)",
  "site.profile.G1": "G1 — Business hours Mo–Fr",
  "site.profile.L0": "L0 — Agriculture",
  "site.deleteSurface": "Delete surface",
  "site.addSurface": "＋ Add surface",
  "site.detectBuilding": "🏠 Detect building",
  "site.detectTitle": "Import the real building outline from OpenStreetMap (keyless)",
  "site.aiSegments": "✨ AI roof segments",
  "site.aiTitle": "AI roof segments with pitch & azimuth (Google Solar API, key required)",
  "site.surfaceName": "Surface name",
  "site.kind.pitched_roof": "Pitched roof",
  "site.kind.flat_roof": "Flat roof",
  "site.kind.ground": "Ground",
  "site.kind.carport": "Carport",
  "site.kind.facade": "Facade",
  "site.roofN": "Roof",
  "site.drawBoundary": "✏️ Draw boundary",
  "site.obstacle": "⛔ Obstacle",
  "site.azimuth": "Azimuth",
  "site.tilt": "Tilt",
  "site.grid": "↻ Module grid", // E60: explicit — never confusable with "rotate view" (camera)
  "site.setback": "Setback",
  "site.clearSurface": "Clear surface",
  "site.legendSurfaces": "surface(s) · active:",
  "site.legendPts": "pts",
  "site.legendObstacles": "obstacles",
  "site.imagery": "Imagery: Esri World Imagery (live)",
  "site.save": "Save site & surfaces",
  "site.continueDesign": "Continue: Design →",
  "site.saved": "✓ Site & surfaces saved.",
  "site.addressNotFound": "Address not found — check spelling or set coordinates manually.",
  "site.detected": "✓ {n} surface(s) detected via {src} — adjust corners on the imagery, then save.",
  "site.detectedOsm": "OpenStreetMap footprint",
  "site.detectedGoogle": "Google Solar",
  // E66: the banner must name the TRUE source — official results were
  // captioned as OSM because only two providers were known here
  "site.detectedOfficial": "official geometry (state survey)",
  // E67 — the chain says why better sources didn't answer
  "site.detectLoading": "Loading official geometry — the first fetch of a tile can take up to a minute…",
  // E69 — the geocoder resolved a DIFFERENT postcode than typed (wrong Marktplatz)
  "site.plzMismatch": "Warning: the address resolved to postcode {resolved}, but {typed} was entered. Check the map position before continuing.",
  // E70 — Google returns only a bounding box per segment, no outline
  "site.googleApproxExtent": "approximate extent — adjust the outline before tiling",
  "site.chainOfficialNoTile": "official geometry: no tile for this location",
  "site.chainOfficialNotReady": "official geometry: not yet enabled for this state",
  "site.chainGoogleNotConfigured": "Google Solar: not configured",
  "site.chainGoogleNoCoverage": "Google Solar: no coverage at this location",
  "site.detectUnavailable":
    "Automatic building detection is currently unavailable — please draw the outline manually. All planning functions remain fully available.",
  "site.pickBuilding": "Several buildings match this address — their outlines are drawn on the imagery. Please pick the correct building.",
  "site.candidate": "Candidate",
  "site.pickThis": "Use this building",
  "site.candidateMeta": "{area} m² roof area · {planes} roof plane(s) · {dist} m from the address point",

  "design.title": "Design — components & layout",
  "design.hint": "All roof surfaces feed one inverter. Click a module to remove it; drag a module to move it (red = invalid position). The grid follows each surface's rotation from the Site step.",
  "design.noSurfaces": "No surfaces drawn yet — go back to Site & Roof first.",
  "design.edit": "EDIT",
  "design.portrait": "▯ Portrait",
  "design.landscape": "▭ Landscape",
  "design.rowGap": "Row gap",
  "design.reset": "↺ Reset ({removed} removed, {moved} moved)",
  "design.modules": "modules",
  "design.legendClick": "Click = remove/restore · Drag = move (1 cm snap) · red = invalid position",
  "design.pvModule": "PV Module",
  "design.powerClass": "Power class",
  "design.inverter": "Inverter",
  "design.integratedIn": "⚡ Integrated in",
  "design.integratedMeta": "kW AC hybrid · {n} MPPT · included in battery price",
  "design.hybrid": "hybrid",
  "design.string": "string",
  "design.suggested": "Suggested: {n}× for {kwp} kWp total (DC/AC {ratio})",
  "design.batteryOperation": "Battery & operation",
  "design.noBattery": "No battery",
  "design.noBatteryMeta": "Feed all surplus into the grid",
  "design.hybridIncl": "hybrid incl. {kw} kW inverter",
  "design.acCoupled": "AC-coupled",
  "design.dispatch": "Battery operation strategy",
  "design.dispatch.self": "Maximize self-consumption (flat tariff)",
  "design.dispatch.price": "Price-optimized (dynamic tariff, e.g. Tibber/aWATTar)",
  "design.mlpeTitle": "🔬 Module-level electronics (MLPE)",
  "design.mlpeHint": "Microinverters replace the string inverter entirely (AC modules, per-module MPPT, no series strings — needs AC-coupled storage). Optimizers pair with a string inverter and recover series-mismatch losses on shaded roofs. SMA ShadeFix and Fronius Dynamic Peak Manager are inverter firmware — already active with those brands at no extra cost.",
  "design.micro": "⚡ Microinverter (replaces string inverter)",
  "design.microNone": "— none (string inverter) —",
  "design.perUnit": "€/unit",
  "design.optimizer": "🔧 DC optimizer (per module, with string inverter)",
  "design.optimizerNone": "— none —",
  "design.requires": "requires",
  "design.anyInverter": "any inverter",
  "design.perModule": "€/module",
  "design.mlpeAsk": "🧠 Should this design use MLPE? (AI Engineer)",
  "design.saveFirst": "save a design first",
  "design.mlpeSummary": "Shade dispersion {shade} % → series mismatch {mismatch} % on a string architecture. Baseline: {arch}, {kwh} kWh/a, NPV {npv}.",
  "design.col.option": "Option",
  "design.col.gain": "Gain",
  "design.col.addedCost": "Added cost",
  "design.col.npv": "ΔNPV",
  "design.col.payback": "ΔPayback",
  "design.col.verdict": "Verdict",
  "design.requiredArch": "required (architecture)",
  "design.applyRecommendation": "Apply recommendation to design",
  "design.ecoTitle": "🏠 Energy ecosystem",
  "design.ecoHint": "Wallbox, heat pump, energy manager and meter are full components: they change the hourly load simulation, autarky and payback, and flow into costing, BOM and purchase orders — exactly like modules, inverters and batteries.",
  "design.evCharger": "🚗 EV charger (wallbox)",
  "design.none": "— none —",
  "design.pvSurplus": "PV-surplus",
  "design.heatPump": "🔥 Heat pump",
  "design.ems": "🧠 Energy manager (EMS / Smart Home)",
  "design.allBrands": "all brands",
  "design.included": "included",
  "design.meter": "⚡ Energy meter",
  "design.meterGeneric": "— generic (auto in accessories) —",
  "design.thermalKw": "kW th.",
  "design.adv.dcCoupled": "{model} is DC-coupled — microinverter systems need AC-coupled storage (e.g. sonnenBatterie).",
  "design.adv.optBrand": "{model} only works with {brand} inverters (architecture coupling).",
  "design.adv.solaredge": "SolarEdge architecture requires optimizers on every module — select S500B/S650B.",
  "design.adv.emsBrand": "{model} only controls {brands} systems — with {brand} choose an open EMS (Home Assistant, OpenEMS, Loxone).",
  "design.adv.surplusCharging": "{model} supports PV-surplus charging — add an energy manager to activate it (shifts ~50 % of charging into the solar window).",
  "design.adv.sgReady": "{model} is SG-Ready — an energy manager enables PV-optimized preheating.",
  "design.adv.surplusActive": "Surplus control active: EV charging and/or heat-pump preheating follow PV production in the simulation.",
  "design.adv.notReleased": "{battery} is not released for {inverter}. Choose a compatible battery or an AC-coupled system (Tesla, sonnen).",
  "design.adv.containsOwn": "{model} contains its own {kw} kW hybrid inverter — no separate PV inverter needed.",
  "design.adv.acWorksAny": "{model} is AC-coupled — works with any inverter.",
  "design.adv.compatibleWith": "{model} is compatible with {inverter}.",
  "design.saveVariant": "Save as new variant",
  "design.saveDesign": "Save design",
  "design.continueSim": "Continue: Simulation →",
  "design.designSaved": "✓ Design saved as new version.",
  "design.versions": "Versions:",
  "design.manual": "manual",
  "design.variantName": "Variant",

  "ai.title": "✨ AI Auto-Designer",
  "ai.hint": "Sweeps the entire catalogue — inverter power classes (DC/AC target 1.2), hybrid + battery pairs sized to your consumption, integrated systems like Tesla Powerwall 3, and both dispatch strategies. Every candidate runs the full 8,760-hour simulation and is ranked by NPV. Accepting creates a new design variant; your manual design is never overwritten.",
  "ai.objective": "Objective",
  "ai.obj.npv": "Maximum lifetime profit (NPV)",
  "ai.obj.payback": "Fastest payback",
  "ai.obj.autarky": "Maximum self-sufficiency",
  "ai.obj.cost": "Lowest investment",
  "ai.obj.production": "Maximum production",
  "ai.budget": "Budget cap € (optional)",
  "ai.budgetPlaceholder": "e.g. 20000",
  "ai.maxPayback": "Max payback yrs (optional)",
  "ai.maxPaybackPlaceholder": "e.g. 6",
  "ai.run": "▶ Run AI Engineer",
  "ai.rerun": "↻ Re-run AI Engineer",
  "ai.evaluated": "{n} configurations simulated · module: {module} · weather: {weather}",
  "ai.synthetic": "synthetic climatology",
  "ai.col.system": "System",
  "ai.col.autarky": "Autarky",
  "ai.col.invest": "Invest",
  "ai.col.npv": "NPV",
  "ai.col.payback": "Payback",
  "ai.you": "you",
  "ai.yourBaseline": "Your manual design — baseline for comparison",
  "ai.best": "best",
  "ai.stringsInvalid": "strings invalid",
  "ai.outsideConstraints": "outside constraints",
  "ai.accept": "Accept as AI design",
  "ai.accepted": "Accepted.",
  "ai.createdVariant": "Created \"{name}\" as a new AI variant — your manual design is untouched. Open Simulation or Accessories to inspect it.",
  "ai.alternatives": "Recommended alternatives & trade-offs",
  "ai.sensitivity": "Sensitivity: electricity price (winner, full re-simulation)",
  "ai.col.scenario": "Price scenario",
  "ai.col.benefit1": "Benefit yr 1",
  "ai.today": "today",

  "pkg.title": "Accessories & installation package",
  "pkg.hint": "Quantities are derived from your actual layout ({info}) — rails from row lengths, clamps from segments, connectors from strings, protection from the inverter. Every line shows its rule and stays editable.",
  "pkg.noDesign": "Create a design first — the package is derived from your layout and string plan.",
  "pkg.mainHardware": "Main hardware",
  "pkg.hardwareTotal": "Hardware total",
  "pkg.cat.mounting": "Mounting system",
  "pkg.cat.dc": "DC electrical",
  "pkg.cat.ac": "AC electrical & protection",
  "pkg.cat.monitoring": "Monitoring & battery",
  "pkg.col.item": "Item",
  "pkg.col.qty": "Qty",
  "pkg.col.unit": "Unit €",
  "pkg.col.total": "Total €",
  "pkg.optional": "optional",
  "pkg.tileHardware": "Main hardware",
  "pkg.tileAccessories": "Accessories & small parts",
  "pkg.tileTotal": "Complete installation package",
  "pkg.saveChanges": "Save {n} change(s)",
  "pkg.saved": "Saved",
  "pkg.autoQty": "↺ Auto quantities",
  "pkg.continueSim": "Continue: Simulation →",
  "pkg.priceHint": "Accessory prices are wholesale defaults — override any of them in your Price book (key \"accessory:<sku>\"). The full package flows into the BOM CSV and your wholesaler purchase orders.",

  "sim.title": "Energy & financial simulation",
  "sim.hint": "Hourly simulation: PVGIS satellite irradiation → PV model → load profile → battery dispatch → EEG 2026 financials (incl. Solarspitzengesetz).",
  "sim.weather.pvgis": "PVGIS SARAH3 (satellite, recommended)",
  "sim.weather.synthetic": "Synthetic climatology (offline/demo)",
  "sim.tariffTitle": "Dynamic tariff supplier",
  "sim.tariff.generic": "Dynamic tariff: generic",
  "sim.run": "▶ Run simulation",
  "sim.running": "Simulating…",
  "sim.createDesignFirst": "Create a design first.",
  "sim.annualProduction": "Annual production",
  "sim.performanceRatio": "Performance ratio",
  "sim.selfSufficiency": "Self-sufficiency",
  "sim.selfConsumed": "Self-consumed",
  "sim.gridFeedIn": "Grid feed-in",
  "sim.monthlyProduction": "Monthly production",
  "sim.payback": "Payback",
  "sim.investment": "Investment",
  "sim.vat0": ", 0% VAT",
  "sim.benefitYear1": "Benefit year 1",
  "sim.npv25": "NPV (25 yr)",
  "sim.irr": "IRR",
  "sim.lcoe": "LCOE / kWh",
  "sim.cashflow": "Cumulative cash flow — 25 years",
  "sim.dynTariff": "Dynamic tariff analysis — {provider} (EPEX day-ahead)",
  "sim.avgSpot": "Avg. spot price / kWh",
  "sim.negHours": "Negative-price hours",
  "sim.s51Share": "Feed-in hit by §51 (exact)",
  "sim.importDynamic": "Import cost dynamic /yr (flat: {flat})",
  "sim.arbitrage": "Battery arbitrage /yr",
  "sim.extraBenefit": "Extra benefit /yr vs flat tariff",
  "sim.dynNote": "Feed-in remuneration already excludes negative-price quarter-hours per Solarspitzengesetz — computed exactly from the hourly price series, not a flat haircut. Planning series is EPEX-calibrated; production connects live aWATTar/ENTSO-E data.",
  "sim.inverterBanks": "Inverter banks",
  "sim.col.bank": "Bank",
  "sim.col.inverter": "Inverter",
  "sim.col.strings": "Strings",
  "sim.ok": "ok",
  "sim.check": "check",
  "sim.electricalDesign": "Electrical design",
  "sim.stringsLine": "{n} string(s) × {m} modules · Voc(cold) {voc} V · DC/AC {ratio}",
  "sim.valid": "valid",
  "sim.invalid": "invalid",
  "sim.costBreakdown": "Cost breakdown",
  "sim.totalNet": "Total (net{vat})",
  "sim.vatCite": ", 0% VAT §12 UStG",
  "sim.bomCsv": "⬇ BOM CSV",
  "sim.cashflowCsv": "⬇ Cash-flow CSV",
  "sim.continueReg": "Continue: Regulation →",

  "perm.title": "German regulation & process assistant",
  "perm.hint": "Checklist generated from your simulated system size, project type and Bundesland. Preparation support — final approval rests with DSO, authority and certified installer.",
  "perm.generate": "⚙ Generate checklist from simulation",
  "perm.kind.DSO_GRID_APPLICATION": "Netzanmeldung (grid application)",
  "perm.kind.MASTR_REGISTRATION": "Marktstammdatenregister",
  "perm.kind.SMART_METER_ORDER": "Smart meter (iMSys) + Steuerbox",
  "perm.kind.STRUCTURAL_CHECK": "Structural / roof load check",
  "perm.kind.BUILDING_PERMIT": "Building permit (B-Plan)",
  "perm.kind.DIRECT_MARKETING_CONTRACT": "Direktvermarktung contract",
  "perm.kind.EEG_TENDER": "BNetzA tender (≥1 MWp)",
  "perm.kind.COMPLETION_NOTICE": "Completion notice (Fertigmeldung)",
  "perm.kind.METER_CHANGE": "Meter change",
  "perm.pkgTitle": "📑 Netzanmeldung package",
  "perm.pkgHint": "3-page PDF from your current design: DSO data sheet (VDE-AR-N 4105) with MaStR checklist, single-line diagram with sized cables and protection (LS/SPD/gPV), and the string plan + cable schedule with voltage-drop calculation.",
  "perm.download": "⬇ Download permit package (PDF)",
  "perm.continueProposals": "Continue: Proposals →",

  "prop.title": "Proposals",
  "prop.hint": "Branded PDF with charts and signature block; send issues a public e-signature link — customer acceptance flips the project to ACCEPTED automatically.",
  "prop.create": "＋ Create proposal from latest simulation",
  "prop.simFirst": "Run a simulation first.",
  "prop.signingLink": "Signing link (send to your customer):",
  "prop.copy": "⧉ Copy",
  "prop.col.number": "Number",
  "prop.col.status": "Status",
  "prop.col.net": "Net",
  "prop.col.gross": "Gross",
  "prop.col.validUntil": "Valid until",
  "prop.pdf": "⬇ PDF",
  "prop.send": "✉ Send for signature",

  "proc.title": "Procurement",
  "proc.hint": "One click turns the design's installation package into a purchase order for your wholesaler — every module, inverter, battery, rail, clamp and connector with net prices from your price book.",
  "proc.newOrder": "New order",
  "proc.supplierOptional": "— supplier (optional) —",
  "proc.addSupplierPlaceholder": "add supplier…",
  "proc.add": "+ Add",
  "proc.createOrder": "Create order from package",
  "proc.created": "Purchase order created from the complete installation package (hardware + all accessories).",
  "proc.order": "Order",
  "proc.positions": "positions",
  "proc.net": "net",
  "proc.noSupplier": "no supplier",
  "proc.ordered": "ordered",
  "proc.delivered": "delivered",
  "proc.hide": "Hide",
  "proc.poCsv": "⬇ PO CSV",
  "proc.markOrdered": "Mark ordered",
  "proc.markDelivered": "Mark delivered",
  "proc.partialDelivery": "Partial delivery",
  "proc.cancel": "Cancel",
  "proc.noOrders": "No orders yet.",
  "proc.continueInstall": "Continue: Installation →",

  "inst.title": "Installation",
  "inst.hint": "Schedule the crew, work the task board through to commissioning, and record the E.8 protocol data.",
  "inst.schedule": "Schedule",
  "inst.crew": "Crew",
  "inst.start": "Start",
  "inst.end": "End",
  "inst.updatePlan": "Update plan",
  "inst.createPlan": "Create plan + task board",
  "inst.planSaved": "Installation plan saved — task board generated from the design (battery-aware).",
  "inst.taskBoard": "Task board",
  "inst.done": "done",
  "inst.status.OPEN": "Open",
  "inst.status.IN_PROGRESS": "In progress",
  "inst.status.DONE": "Done",
  "inst.status.BLOCKED": "Blocked",
  "inst.commissioning": "Commissioning (E.8)",
  "inst.commissioned": "commissioned",
  "inst.electrician": "Electrician",
  "inst.registrationNo": "Registration no.",
  "inst.meterNumber": "Meter number",
  "inst.mastrNumber": "MaStR number",
  "inst.save": "Save",
  "inst.markCommissioned": "Mark commissioned now",
  "inst.commissioningSaved": "Commissioning data saved.",
  "inst.commissioningDone": "Commissioning recorded — remember the MaStR registration deadline (1 month).",
  "inst.dateFormat": "DD.MM.YYYY",
  "inst.plannedFor": "planned",
};

export const MESSAGES = { de, en } as const;
export type Locale = keyof typeof MESSAGES;
export type MessageKey = MessageKeyInternal;
